import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Target, 
  TrendingUp, 
  CheckCircle,
  Clock,
  Lightbulb,
  BarChart3,
  Filter
} from 'lucide-react';
import { outcomeTracker } from '../lib/outcomeTracker';
import { OutcomeCard } from '../components/outcomes/OutcomeCard';
import { CreateOutcomeModal } from '../components/outcomes/CreateOutcomeModal';
import { EditOutcomeModal } from '../components/outcomes/EditOutcomeModal';
import { DeleteOutcomeDialog } from '../components/outcomes/DeleteOutcomeDialog';
import { ShareAsStoryModal } from '../components/outcomes/ShareAsStoryModal';
import { FrameworkROIChart } from '../components/outcomes/FrameworkROIChart';
import { MetricCard } from '../components/analytics/MetricCard';
import { InsightCard } from '../components/analytics/InsightCard';
import type { Outcome, OutcomeStatus, OutcomeCategory } from '../types/outcomes';

export const Outcomes: React.FC = () => {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<Outcome | null>(null);
  const [statusFilter, setStatusFilter] = useState<OutcomeStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<OutcomeCategory | 'all'>('all');
  const [activeTab, setActiveTab] = useState<'active' | 'completed' | 'all' | 'roi'>('active');

  useEffect(() => {
    loadOutcomes();
  }, []);

  const loadOutcomes = () => {
    const data = outcomeTracker.getOutcomes();
    setOutcomes(data);
  };

  const handleCreateOutcome = (data: any) => {
    outcomeTracker.createOutcome({
      ...data,
      status: 'planning' as OutcomeStatus,
    });
    loadOutcomes();
  };

  const handleEditOutcome = (outcome: Outcome) => {
    setSelectedOutcome(outcome);
    setIsEditModalOpen(true);
  };

  const handleSaveOutcome = (updates: Partial<Outcome>) => {
    if (selectedOutcome) {
      outcomeTracker.updateOutcome(selectedOutcome.id, updates);
      loadOutcomes();
    }
  };

  const handleDeleteOutcome = (outcome: Outcome) => {
    setSelectedOutcome(outcome);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedOutcome) {
      outcomeTracker.deleteOutcome(selectedOutcome.id);
      loadOutcomes();
      setSelectedOutcome(null);
    }
  };

  const handleShareOutcome = (outcome: Outcome) => {
    setSelectedOutcome(outcome);
    setIsShareModalOpen(true);
  };

  const handleShare = (storyData: any) => {
    // Add to team stories in collaboration tracker
    const { collaborationTracker } = require('../lib/collaborationTracker');
    collaborationTracker.addStory({
      ...storyData,
      author: 'You',
      date: new Date().toISOString(),
      likes: 0,
      comments: [],
    });

    // Show success message
    alert('🎉 Story shared with your team! Check the Team page to see it.');
    
    // Close modal
    setIsShareModalOpen(false);
    setSelectedOutcome(null);
  };

  const handleOutcomeClick = (outcome: Outcome) => {
    // TODO: Navigate to outcome detail page or open detail modal
    console.log('Outcome clicked:', outcome);
  };

  const stats = outcomeTracker.getStats();
  const insights = outcomeTracker.getInsights();
  const frameworkROI = outcomeTracker.getFrameworkROI();

  const filteredOutcomes = outcomes.filter(outcome => {
    if (activeTab === 'active' && outcome.status !== 'in-progress') return false;
    if (activeTab === 'completed' && outcome.status !== 'completed') return false;
    if (statusFilter !== 'all' && outcome.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && outcome.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900 mb-2">Outcomes Dashboard</h1>
          <p className="text-charcoal-600">
            Track how frameworks drive real-world results and measure your impact
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Outcome
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Outcomes"
          value={stats.total}
          subtitle="All tracked outcomes"
          icon={<Target className="h-6 w-6 text-consulting-slate-blue-600" />}
          color="primary"
        />
        <MetricCard
          title="Active"
          value={stats.activeOutcomes}
          subtitle="Currently in progress"
          icon={<TrendingUp className="h-6 w-6 text-sage-600" />}
          color="sage"
        />
        <MetricCard
          title="Completed"
          value={stats.byStatus.completed}
          subtitle={`${stats.completionRate}% completion rate`}
          icon={<CheckCircle className="h-6 w-6 text-[var(--mustard)]" />}
          color="amber"
        />
        <MetricCard
          title="Avg Progress"
          value={`${stats.averageProgress}%`}
          subtitle="Across all outcomes"
          icon={<Clock className="h-6 w-6 text-coral-600" />}
          color="coral"
        />
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-6 w-6 text-[var(--mustard)]" />
            <h2 className="text-2xl font-bold text-charcoal-900">Key Insights</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {insights.map((insight, idx) => (
              <InsightCard key={idx} insight={insight} />
            ))}
          </div>
        </section>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-[var(--rule)]">
        <nav className="flex gap-6">
          {[
            { id: 'active', label: 'Active', count: stats.activeOutcomes },
            { id: 'completed', label: 'Completed', count: stats.byStatus.completed },
            { id: 'all', label: 'All Outcomes', count: stats.total },
            { id: 'roi', label: 'Framework ROI', icon: BarChart3 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`
                flex items-center gap-2 px-1 py-3 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-consulting-slate-blue-600 text-consulting-slate-blue-700'
                  : 'border-transparent text-charcoal-600 hover:text-charcoal-900'
                }
              `}
            >
              {tab.icon && <tab.icon className="h-4 w-4" />}
              {tab.label}
              {tab.count !== undefined && (
                <span className={`
                  px-2 py-0.5 rounded-full text-xs
                  ${activeTab === tab.id
                    ? 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700'
                    : 'bg-[var(--hover-tint)] text-charcoal-600'
                  }
                `}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="animate-fadeIn">
        {activeTab === 'roi' ? (
          /* Framework ROI View */
          <section>
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-6 w-6 text-consulting-slate-blue-600" />
              <h2 className="text-2xl font-bold text-charcoal-900">Framework Return on Investment</h2>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="text-sm text-charcoal-600 mb-6">
                Frameworks ranked by their impact across outcomes (based on usage frequency, outcomes affected, and effectiveness ratings)
              </p>
              <FrameworkROIChart data={frameworkROI} limit={15} />
            </div>
          </section>
        ) : (
          /* Outcomes List View */
          <>
            {/* Filters */}
            {(statusFilter !== 'all' || categoryFilter !== 'all') && (
              <div className="flex items-center gap-3 mb-6">
                <Filter className="h-4 w-4 text-charcoal-500" />
                <span className="text-sm text-charcoal-600">Filters:</span>
                {statusFilter !== 'all' && (
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="px-3 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-700 text-sm rounded-full hover:bg-consulting-slate-blue-200 transition-colors"
                  >
                    Status: {statusFilter} ×
                  </button>
                )}
                {categoryFilter !== 'all' && (
                  <button
                    onClick={() => setCategoryFilter('all')}
                    className="px-3 py-1 bg-sage-100 text-sage-700 text-sm rounded-full hover:bg-sage-200 transition-colors"
                  >
                    Category: {categoryFilter} ×
                  </button>
                )}
              </div>
            )}

            {/* Outcomes Grid */}
            {filteredOutcomes.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredOutcomes.map(outcome => (
                  <OutcomeCard
                    key={outcome.id}
                    outcome={outcome}
                    onClick={() => handleOutcomeClick(outcome)}
                    onShare={() => handleShareOutcome(outcome)}
                    onEdit={() => handleEditOutcome(outcome)}
                    onDelete={() => handleDeleteOutcome(outcome)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-[var(--surface)] border-2 border-dashed border-[var(--rule)]">
                <Target className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-charcoal-900 mb-2">
                  {activeTab === 'active' ? 'No Active Outcomes' : 
                   activeTab === 'completed' ? 'No Completed Outcomes Yet' : 
                   'No Outcomes Yet'}
                </h3>
                <p className="text-charcoal-600 mb-6">
                  {activeTab === 'active' 
                    ? 'Start tracking an outcome to see it here'
                    : 'Create your first outcome to start tracking real-world impact'}
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Outcome
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Outcome Modal */}
      <CreateOutcomeModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateOutcome}
      />

      {/* Edit Outcome Modal */}
      {selectedOutcome && (
        <EditOutcomeModal
          outcome={selectedOutcome}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedOutcome(null);
          }}
          onSave={handleSaveOutcome}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteOutcomeDialog
        outcome={selectedOutcome}
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedOutcome(null);
        }}
        onConfirm={confirmDelete}
      />

      {/* Share as Story Modal */}
      {selectedOutcome && (
        <ShareAsStoryModal
          outcome={selectedOutcome}
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setSelectedOutcome(null);
          }}
          onShare={handleShare}
        />
      )}
    </div>
  );
};

